// eslint-disable-next-line
import React, { useState, useCallback } from 'react';
import Logger from '../../../shared/Logger';
import { Flex, Button, Modal, Box, theme, Panel } from '@xometry/xometry_loft';
import { useMutation } from '@apollo/client';
import { css } from '@emotion/react';
import {
  usePlaidLink,
  // PlaidLinkOptions,
  // PlaidLinkOnSuccess,
  // PlaidLinkOnSuccessMetadata,
  // PlaidLinkOnExit,
  // PlaidLinkOnExitMetadata,
  // PlaidLinkError,
} from 'react-plaid-link';
import { MSTS_ONBOARDING, WWW_ORIGIN } from '../../../config';
import {
  AddBankAccountQuery,
  // AddBankAccountMutationResult,
  // AddBankAccountMutationVariables,
  // PaymentMethodsQueryResult,
  // PaymentMethodsQueryVariables,
} from '../../../schema/generated/schema';
import CreditCardMethods from '../AddCardModal/CreditCardMethods/index';
import BankAccountMethods from './BankAccountMethods/index';

// export interface Props {
//   paymentMethodModalOpen: boolean;
//   setPaymentMethodModalOpen: (openState: boolean) => void;
//   methodsRefetch: any;
// }

const PaymentMethodModal = (props) => {
  const {
    paymentMethodModalOpen,
    setPaymentMethodModalOpen,
    methodsRefetch,
    linkTokenData,
    linkTokenLoading,
    linkTokenRefetch,
    userID,
    handleMstsPolling,
    openStripeCardModal,
  } = props;

  const [selectedMethod, setSelectedMethod] = useState(null);
  const [newPaymentMethod, setNewPaymentMethod] = useState(null);

  const [addBankAccount] = useMutation(AddBankAccountQuery);

  // https://github.com/plaid/react-plaid-link#examples
  // PlaidLink OnSuccess handler
  const onSuccess = useCallback((public_token, metadata) => {
    // send token and account_ids to server
    const accountIds = metadata.accounts.map((account) => account.id);
    addBankAccount({
      variables: {
        token: public_token,
        accountIds,
      },
    })
      .then(() => {
        methodsRefetch();
      })
      .catch((error) => {
        Logger.error('Error adding bank account', error, {
          component: 'PaymentMethodModal',
          action: 'addBankAccount',
        });
      });
    // eslint-disable-next-line
  }, []);

  const onExit = useCallback(
    (error, metadata) => {
      console.error(error, metadata);
      // handle invalid link token
      if (error != null && error.error_code === 'INVALID_LINK_TOKEN') {
        linkTokenRefetch();
      }
    },
    // eslint-disable-next-line
    []
  );

  const handleStripeCard = () => {
    // We need to show stripe input screen
    setPaymentMethodModalOpen(false);
    setNewPaymentMethod(null);
    openStripeCardModal();
  };

  const config = {
    token: linkTokenData?.getLinkToken.token,
    onSuccess,
    onExit,
  };
  const { open, ready } = usePlaidLink(config);

  const handlePlaid = () => {
    setPaymentMethodModalOpen(false);
    setNewPaymentMethod(null);
    if (ready) open();
  };

  const handleMSTS = () => {
    handleMstsPolling(methodsRefetch);
    window.open(
      `${MSTS_ONBOARDING}/apply?client_reference_id=${userID}&ecommerce_url=${WWW_ORIGIN}/user-settings`
    );
    setPaymentMethodModalOpen(false);
    setNewPaymentMethod(null);
  };

  // Figure out which payment method needs to load next
  const addNewPaymentMethod = (newPaymentMethod) => {
    setSelectedMethod(null);
    if (newPaymentMethod === 'plaid') {
      handlePlaid();
    } else if (newPaymentMethod === 'msts') {
      handleMSTS();
    } else if (newPaymentMethod === 'stripe') {
      handleStripeCard();
    }
  };

  return (
    <>
      <div id="paymentMethodModal" />
      <Modal
        isOpen={paymentMethodModalOpen}
        selector="#paymentMethodModal"
        padding="20px 20px 20px 24px"
        color="#253842"
        fontFamily="Open Sans"
        backgroundColor="white"
        borderRadius="4px"
        border="none"
        boxShadow="0 1px 4px 0 rgba(9,44,71,0.15)"
        width="344px"
        display={paymentMethodModalOpen ? 'initial' : 'none'}
        disableBodyScroll={false}
      >
        <Flex justifyContent="space-between" marginBottom="16px">
          <Box
            fontSize={theme.fontSizes[3]}
            fontWeight={theme.fontWeights.semibold}
            lineHeight="28px"
            color={theme.colors.darkBlues[90]}
          >
            Add a Payment Method
          </Box>
          <Box
            fontSize={theme.fontSizes[4]}
            lineHeight={theme.lineHeights[3]}
            textAlign="right"
            color={theme.colors.darkBlues[100]}
            onClick={() => setPaymentMethodModalOpen(false)}
            css={css`
              &:hover {
                cursor: pointer;
              }
            `}
          >
            <i className="fal fa-times" />
          </Box>
        </Flex>

        <Panel
          kind="outlineWhite"
          borderColor={theme.colors.darkBlues[10]}
          marginBottom="16px"
          onClick={() => setSelectedMethod('stripeAccount')}
          css={
            selectedMethod !== 'stripeAccount' &&
            css`
              &:hover {
                cursor: pointer;
              }
            `
          }
          backgroundColor={
            selectedMethod === 'stripeAccount' && `${theme.colors.blues[10]}`
          }
        >
          <Flex
            alignItems="center"
            fontSize={theme.fontSizes[2]}
            marginLeft="5px"
            color={theme.colors.darkBlues[75]}
          >
            <i className="fa fa-credit-card" aria-hidden="true" />
            <Box
              color={theme.colors.darkBlues[100]}
              fontWeight={theme.fontWeights.semibold}
              lineHeight={theme.lineHeights[3]}
              marginLeft="10px"
            >
              Credit or Debit Card
            </Box>
          </Flex>
          {selectedMethod === 'stripeAccount' && !linkTokenLoading && (
            <Box marginTop="8px">
              <CreditCardMethods
                newPaymentMethod={newPaymentMethod}
                setNewPaymentMethod={setNewPaymentMethod}
              />
            </Box>
          )}
        </Panel>

        <Panel
          kind="outlineWhite"
          borderColor={theme.colors.darkBlues[10]}
          marginBottom="16px"
          onClick={() => setSelectedMethod('bankAccount')}
          css={
            selectedMethod !== 'bankAccount' &&
            css`
              &:hover {
                cursor: pointer;
              }
            `
          }
          backgroundColor={
            selectedMethod === 'bankAccount' && `${theme.colors.blues[10]}`
          }
        >
          <Flex
            alignItems="center"
            fontSize={theme.fontSizes[2]}
            marginLeft="5px"
            color={theme.colors.darkBlues[75]}
          >
            <i className="fas fa-landmark" />
            <Box
              color={theme.colors.darkBlues[100]}
              fontWeight={theme.fontWeights.semibold}
              lineHeight={theme.lineHeights[3]}
              marginLeft="10px"
            >
              Bank Account
            </Box>
          </Flex>
          {selectedMethod === 'bankAccount' && !linkTokenLoading && (
            <Box marginTop="8px">
              <BankAccountMethods
                newPaymentMethod={newPaymentMethod}
                setNewPaymentMethod={setNewPaymentMethod}
              />
            </Box>
          )}
        </Panel>

        {/*
        COMMENTING OUT MSTS CODE BELOW
          This is being disabled as a fast stopgap to address
          various users utilizing this payment method when they
          were not the intended audience currently. Jira ticket
          has linked designs docs for when this feature should
          go live again.
      */}
        {/* {!serializedPaymentsMethods.find(
          (paymentMethod) => paymentMethod.type === "msts"
        ) && (
          <Panel
            kind="outlineWhite"
            borderColor={theme.colors.darkBlues[10]}
            marginBottom="16px"
            onClick={() => {
              setSelectedMethod("msts");
              setNewPaymentMethod("msts");
            }}
            css={
              selectedMethod !== "msts" &&
              css`
                &:hover {
                  cursor: pointer;
                }
              `
            }
            backgroundColor={
              selectedMethod === "msts" && `${theme.colors.blues[10]}`
            }
          >
            <Flex
              alignItems="center"
              fontSize={theme.fontSizes[2]}
              marginLeft="5px"
              color={theme.colors.darkBlues[75]}
            >
              <img
                src="https://s3.amazonaws.com/assets.xometry.com/images/Logomark_Xometry_Dark.svg"
                alt="Xometry X Logo"
                height="21px"
              />
              <Box
                color={theme.colors.darkBlues[100]}
                fontWeight={theme.fontWeights.semibold}
                lineHeight={theme.lineHeights[3]}
                marginLeft="10px"
              >
                Apply for Net 30 Terms
              </Box>
            </Flex>
            {selectedMethod === "msts" && (
              <Box marginTop="8px">
                <Box
                  fontSize={theme.fontSizes[2]}
                  fontWeight={theme.fontWeights.semibold}
                  lineHeight="24px"
                  color={theme.colors.darkBlues[100]}
                >
                  Xometry Flex with Invoice
                </Box>
                <Box
                  fontSize={theme.fontSizes[0]}
                  lineHeight="20px"
                  color={theme.colors.darkBlues[90]}
                >
                  Apply for hassle free Net-30 payment terms that meet your
                  needs.
                </Box>
              </Box>
            )}
          </Panel>
        )} */}

        <Button
          kind="primary"
          scale="large"
          width="100%"
          disabled={!newPaymentMethod}
          onClick={() => addNewPaymentMethod(newPaymentMethod)}
        >
          Continue
        </Button>
      </Modal>
    </>
  );
};

export default PaymentMethodModal;
